import {pick} from 'lodash-es';
import {v4 as uid} from 'uuid';

import {
    TDDFilterExpessionGroupIdentificator,
    TDDFilterExpression,
    TDDFilterExpressionDto,
    TDDFilterOperand,
    TTransformFilterExpressionDtoToArrayFunction
} from '../model/types';

/**
 * Преобразует DTO фильтра в массивное представление выражения фильтра
 * 
 * @description Рекурсивно преобразует объект фильтра (DTO) в плоскую структуру массива,
 * где первый элемент - идентификатор группы, а последующие - операнды (правила или вложенные группы).
 * В процессе преобразования каждому элементу присваивается уникальный идентификатор (uid).
 * Операнды сортируются по полю ordinal для сохранения порядка.
 * 
 * @param expressionDto - DTO объект фильтра для преобразования
 * @returns Массивное представление выражения фильтра (TDDFilterExpression), где:
 *          - первый элемент: идентификатор группы 
 *          - последующие элементы: операнды (правила фильтрации или вложенные группы)
 * 
 * @example
 * // Пример входных данных:
 * const dto = {
 *   id: 'group1',
 *   ordinal: 1,
 *   enabled: true,
 *   connectBy: 'AND',
 *   rules: [{ field: 'name', operator: 'eq', value: 'test', ordinal: 1 }],
 *   groups: []
 * };
 * 
 * // Результат:
 * // [
 * //   { id: 'group1', ordinal: 1, enabled: true, connectBy: 'AND', uid: 'uuid-1234' },
 * //   { field: 'name', operator: 'eq', value: 'test', ordinal: 1, uid: 'uuid-5678' }
 * // ]
 */
export const transformFilterExpressionDtoToExpressionArray:
    TTransformFilterExpressionDtoToArrayFunction = (expressionDto: TDDFilterExpressionDto) => {
        const identifier = {
            ...pick(expressionDto, [
                'connectBy', 'enabled', 'id', 'ordinal',
            ]),
            uid: uid(),
        } as TDDFilterExpessionGroupIdentificator;

        const {groups, rules} = expressionDto;

        const operands: TDDFilterOperand[] = rules?.length ? rules.map(
            rule => ({...rule, uid: uid()}),
        ) as TDDFilterOperand[] : [];

        if (groups?.length) {
            groups.forEach(groupDto => {
                operands.push(transformFilterExpressionDtoToExpressionArray(groupDto));
            });
        }

        const operandsSorted = operands.sort((a, b) => {
            const aOrdinal = (Array.isArray(a) ? a[0].ordinal : a.ordinal) ?? 0;
            const bOrdinal = (Array.isArray(b) ? b[0].ordinal : b.ordinal) ?? 0;

            return aOrdinal === bOrdinal ? 0 : aOrdinal - bOrdinal < 0 ? -1 : 1;
        });

        return [
            identifier,
            ...operandsSorted,
        ] as TDDFilterExpression;
    };

